<template>
  <div class="audio-trimmer">
    <Waveform class="audio-trimmer__waveform" :audio-buffer-waveform-data="audioBufferWaveformData"/>
    <div class="audio-trimmer__playhead-overlay"></div>
    <div class="audio-trimmer__playhead-hover-overlay"></div>
    <div 
      class="audio-trimmer__scrubber"
      ref="scrubber"
    >
      <input
        class="audio-trimmer__slider" 
        type="range" 
        v-model.number="audioSeekSliderModel" 
        :min="loopStartTime" 
        :max="loopEndTime" 
        :step="0.01"
        @input="onUpdateAudioSlider()"
        @change="onChangeAudioSlider()"
        @mouseover="inputHover = true"
        @mouseleave="inputHover = false"
      >
    </div>
    <div class="audio-trimmer__bracket"></div>
    <div class="audio-trimmer__inner-bracket"></div>
    <div class="audio-trimmer__middle-handle">
      <card-corner :class="['middle-handle__curve', 'middle-handle__left-curve', {'middle-handle__curve--hidden': !showMiddleBracketCurve}]"></card-corner>
      <gripper class="audio-trimmer__gripper" ref="leftGripper"/>
      <input
        class="audio-trimmer__duration-input"
        type="text"
        required
        :value="loopDurationInputModel"
        @blur="onChangeLoopDuration"
        @keydown.prevent="onUpdateLoopDuration"
        @focus="loopDurationInputModel = ''"
      />
      <gripper class="audio-trimmer__gripper" ref="rightGripper"/>
      <card-corner :class="['middle-handle__curve', 'middle-handle__right-curve', {'middle-handle__curve--hidden': !showMiddleBracketCurve}]"></card-corner>
    </div>
    <div class="audio-trimmer__outside-loop-overlay audio-trimmer__outside-loop-overlay--left"></div>
    <label class="audio-trimmer__time-label" :style="loopStartLabelPositionStyle">{{loopStartLabel}}</label>
    <div class="audio-trimmer__handle audio-trimmer__handle--left" ref="leftBracketElement">
      <div class="audio-trimmer__grabber"></div>
    </div>
    <div class="audio-trimmer__outside-loop-overlay audio-trimmer__outside-loop-overlay--right"></div>
    <label class="audio-trimmer__time-label" :style="loopEndLabelPositionStyle">{{loopEndLabel}}</label>
    <div class="audio-trimmer__handle audio-trimmer__handle--right" ref="rightBracketElement">
      <div class="audio-trimmer__grabber"></div>
    </div>
  </div>
</template>
<script setup lang="ts">
import Waveform from '@/components/preview/Waveform.vue'
import useAudioEngine from '@/composables/UseAudioEngine'
import timeFormat from '@/utils/time-format'
import CardCorner from '@/assets/CardCorner.svg'
import Gripper from '@/components/common/Gripper.vue'
import { ComponentPublicInstance, computed, onMounted, Ref, ref, watch } from 'vue'

type Bracket = 'left' | 'right'

const props = defineProps<{
  currentTime: number
  totalTime: number
  audioBufferWaveformData: Float32Array
  resetTrimFlag: boolean
}>()

const { seekAudio, trimAudio, untrimmedAudioDurationSeconds, loopStartTime, loopEndTime, loadState } = useAudioEngine()

const handleWidth = 10
const middleHandleWidth = 62
const minLoopLength = computed(() => Math.ceil(scrubberWidth.value / untrimmedAudioDurationSeconds.value)) // calculates how wide in pixels 1 second is based on scrubber width

const audioSeekSliderModel = ref(0)
const sliderDragging = ref(false)
const scrubber = ref<HTMLDivElement | null>()
const leftBracketElement = ref<HTMLDivElement | null>()
const rightBracketElement = ref<HTMLDivElement | null>()
const leftGripper = ref<ComponentPublicInstance | null>()
const rightGripper = ref<ComponentPublicInstance | null>()

const isDraggingHandle = ref(false)
const inputHover = ref(false)
const durationInputUpdated = ref(false)

const handleCursor = computed(() => isDraggingHandle.value ? 'grabbing' : 'grab')
const pointerCursor = computed(() => isDraggingHandle.value ? 'grabbing' : 'pointer')
const defaultCursor = computed(() => isDraggingHandle.value ? 'grabbing' : 'default')
const textCursor = computed(() => isDraggingHandle.value ? 'grabbing' : 'text')
const untrimmedAudioDurationFormatted = computed(() => timeFormat(untrimmedAudioDurationSeconds.value))
const loopDurationInputModel = ref(untrimmedAudioDurationFormatted.value)
const loopStartPercentage = ref(0) // The percentage through the track that the loop starts
const loopEndPercentage = ref(100) // The percentage through the track that the loop ends
const scrubberWidth = ref(1000)

const loopStartPositionStyle = computed(() => `${loopStartPercentage.value}%`)
const loopEndPositionStyle = computed(() => `${100 - loopEndPercentage.value}%`)
const leftBracketStyle = computed(() => `calc(${loopStartPercentage.value}% - ${handleWidth}px)`)
const rightBracketStyle = computed(() => `calc(${100 - loopEndPercentage.value}% - ${handleWidth}px)`)
const leftInnerBracketStyle = computed(() => `calc(${loopStartPercentage.value}% - ${handleWidth - 8}px)`)
const rightInnerBracketStyle = computed(() => `calc(${100 - loopEndPercentage.value}% - ${handleWidth - 8}px)`)
const centerPosition = computed(() => (loopEndPercentage.value + loopStartPercentage.value) / 2)
const leftMiddleBracketStyle = computed(() => `calc(${centerPosition.value}% - ${middleHandleWidth / 2}px)`)
const rightMiddleBracketStyle = computed(() => `calc((${100 - centerPosition.value}% - ${middleHandleWidth / 2}px)`)
const loopStartLabel = computed(() => timeFormat(loopStartPercentage.value / 100 * untrimmedAudioDurationSeconds.value))
const loopEndLabel = computed(() => timeFormat(loopEndPercentage.value / 100 * untrimmedAudioDurationSeconds.value))
const loopStartLabelPositionStyle = computed(() => {
  if ((loopEndPercentage.value - loopStartPercentage.value) / 100 * scrubberWidth.value > middleHandleWidth + 32) {
    return {
      left: leftBracketStyle.value,
      top: '-19px'
    }
  } else {
    return {
      left: `calc(${centerPosition.value}% - ${middleHandleWidth / 2}px)`,
      top: '-36px'
    }
  }
})
const loopEndLabelPositionStyle = computed(() => {
  if ((loopEndPercentage.value - loopStartPercentage.value) / 100 * scrubberWidth.value > middleHandleWidth + 32) {
    return {
      right: rightBracketStyle.value,
      top: '-19px'
    }
  } else {
    return {
      right: `calc(${100 - centerPosition.value}% - ${middleHandleWidth / 2}px)`,
      top: '-36px'
    }
  }
})
const showMiddleBracketCurve = computed(() => (loopEndPercentage.value - loopStartPercentage.value) / 100 * scrubberWidth.value > middleHandleWidth)
const middleHandleBorderRadiusStyle = computed(() => showMiddleBracketCurve.value ? '4px 4px 0px 0px': '4px')
const playHeadPosition = computed(() => audioSeekSliderModel.value / untrimmedAudioDurationSeconds.value * 100)
const scrubberMousePosition = computed(() => mouseXPosition.value / scrubberWidth.value * 100 + loopStartPercentage.value)
const playHeadHoverOverlayEndPosition = computed(() => { 
  if (inputHover.value && !isDraggingHandle.value && scrubberMousePosition.value < playHeadPosition.value) {
    return `${100 - scrubberMousePosition.value}%`
  }

  return 100 - playHeadPosition.value + '%'
})

const playHeadOverlayStartPositionStyle = computed(() => {
  if (inputHover.value && !isDraggingHandle.value && scrubberMousePosition.value > playHeadPosition.value) {
    return `${scrubberMousePosition.value}%`
  }

  return Math.max(playHeadPosition.value, loopStartPercentage.value) + '%'
})

const onChangeLoopDuration = () => {
  if (durationInputUpdated.value) {
    const loopDuration = getTimeInputSeconds()
    // 0 loop duration indicates an invalid input
    if (loopDuration > 0) {
      // loopEnd must not go beyond the end of the track
      // Add 0.000001 just to account for some rounding errors that could result in the duration being a second off because we are rounding down
      loopEndPercentage.value = Math.min(loopStartPercentage.value + loopDuration / untrimmedAudioDurationSeconds.value * 100 + 0.0000001, 100)
    }
    durationInputUpdated.value = false
  }

  onTrimAudio()
}

const onUpdateLoopDuration = (e: KeyboardEvent) => {
  if (loopDurationInputModel.value.length < 4 && (e.key === '0' || parseInt(e.key))) {
    loopDurationInputModel.value += e.key
    if (loopDurationInputModel.value.length === 1) {
      loopDurationInputModel.value += ':'
    }
    durationInputUpdated.value = true
  } else if (e.key === 'Backspace') {
    loopDurationInputModel.value = loopDurationInputModel.value.length === 2 ? '' : loopDurationInputModel.value.substring(0, loopDurationInputModel.value.length - 1)
  } else if (e.key ==='Enter') {
    onChangeLoopDuration()
  }
}

const getTimeInputSeconds = () => {
  const times = loopDurationInputModel.value.split(':')
  return parseInt(times[0]) * 60 + parseInt(times[1])
}

const onTrimAudio = () => {
  trimAudio(loopStartPercentage.value / 100 * untrimmedAudioDurationSeconds.value, loopEndPercentage.value / 100 * untrimmedAudioDurationSeconds.value)
  loopDurationInputModel.value = timeFormat(getLoopDuration())
}

const resetTrim = () => {
  loopStartPercentage.value = 0
  loopEndPercentage.value = 100
  onTrimAudio()
}

watch(() => props.resetTrimFlag, () => {
  resetTrim()
})

watch(() => loadState.value, () => {
  if (loadState.value === 'loaded') {
    resetTrim()
  }
})
  
watch(() => props.currentTime, () => {
  if (!sliderDragging.value) {
    audioSeekSliderModel.value = props.currentTime + loopStartTime.value
  }
})

const getLoopDuration = () => {
  return (loopEndPercentage.value - loopStartPercentage.value) / 100 * untrimmedAudioDurationSeconds.value
}

const onUpdateAudioSlider = () => {
  sliderDragging.value = true
}

const mouseXPosition = ref(0)
const trackMousePosition = (e: MouseEvent) => {
  mouseXPosition.value = e.offsetX
}

onMounted(() => {
  if (leftBracketElement.value && rightBracketElement.value && scrubber.value && leftGripper.value && rightGripper.value) {
    initBracketDrag(leftBracketElement.value, loopStartPercentage, 'left')
    initBracketDrag(rightBracketElement.value, loopEndPercentage, 'right')
    scrubber.value.onmousemove = trackMousePosition
    leftGripper.value.$el.onmousedown = rightGripper.value.$el.onmousedown = (e: MouseEvent) => {
      if (e.button === 0) {
        isDraggingHandle.value = true
        const leftInitialPosition = loopStartPercentage.value / 100 * scrubberWidth.value
        const start = e.pageX
  
        document.body.onmousemove = (e) => {
          const end = e.pageX
          const diff = end-start
          let newPosition = Math.max(diff + leftInitialPosition, 0)
          // Ensure it's less than the end of the scrubber minus the duration
          const loopDurationPercentage = props.totalTime / untrimmedAudioDurationSeconds.value * 100
          const loopDurationLength = loopDurationPercentage / 100 * scrubberWidth.value
          newPosition = Math.min(newPosition, scrubberWidth.value - loopDurationLength)

          // Convert to percentage
          loopStartPercentage.value = (newPosition / scrubberWidth.value) * 100
          loopEndPercentage.value = loopStartPercentage.value + loopDurationPercentage
        }

        window.onmouseup = () => {
          isDraggingHandle.value = false
          onTrimAudio()
          document.body.onmousemove = null
          window.onmouseup = null
        }
      }
    }

    scrubberWidth.value = scrubber.value.clientWidth
    new ResizeObserver(() => {
      if (scrubber.value) {
        scrubberWidth.value = scrubber.value.clientWidth
      }
    }).observe(scrubber.value)
  }
})

const initBracketDrag = (trimBracket: HTMLDivElement, loopModel: Ref<number>, type: Bracket) => {
  trimBracket.onmousedown = (e) => {
    if (e.button === 0) {
      isDraggingHandle.value = true
      const initialPosition = loopModel.value * scrubberWidth.value / 100
      const start = e.pageX
  
      document.body.onmousemove = (e) => {
        const end = e.pageX
        // Calculate the number of pixels dragged
        const diff = end-start
        // Calculate new position as pixels
        let newPosition = diff + initialPosition
        // Ensure it's less than the end of the scrubber
        if (type === 'left') {
          newPosition = Math.max(newPosition, 0)
          newPosition = Math.min(newPosition, loopEndPercentage.value / 100 * scrubberWidth.value - minLoopLength.value)
        } else if (type === 'right') {
          newPosition = Math.min(newPosition, scrubberWidth.value)
          newPosition = Math.max(newPosition, loopStartPercentage.value / 100 * scrubberWidth.value + minLoopLength.value)
        }

        // Convert to percentage
        loopModel.value = (newPosition / scrubberWidth.value) * 100
        loopDurationInputModel.value = timeFormat(getLoopDuration())
      }

      window.onmouseup = () => {
        isDraggingHandle.value = false
        onTrimAudio()
        document.body.onmousemove = null
        window.onmouseup = null
      }
    }
  }
}

const onChangeAudioSlider = () => {
  sliderDragging.value = false
  seekAudio(audioSeekSliderModel.value - loopStartTime.value)
}

const handleWidthStyle = computed(() => handleWidth + 'px')

</script>
<style lang="scss">
.audio-trimmer { 
  position: relative;
  margin-bottom: 15px;
  margin-left: 10px;
  margin-right: 10px;
  overflow: visible;
  cursor: v-bind(defaultCursor);
  
  &__scrubber {
    height: 55px;
    position: absolute;
    left: 0px;
    top: -1px;
    width: 100%;
    opacity: 0;
  }

  &__slider {
    position: absolute;
    left: v-bind(loopStartPositionStyle);
    right: v-bind(loopEndPositionStyle);
    top: 0px;
    height: 100%;
    cursor: v-bind(pointerCursor);
    margin: 0px;
    -webkit-appearance: none;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 1px;
      height: 10px;
    }
  }

  &__middle-handle {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    position: absolute;
    background-color: $vibrant-yellow;
    padding-top: 2px;
    top: -20px;
    height: 20px;
    left: v-bind(leftMiddleBracketStyle);
    right: v-bind(rightMiddleBracketStyle);
    border-radius: v-bind(middleHandleBorderRadiusStyle);
    transition: border-radius 150ms $animationCurve;

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    .middle-handle__curve {
      position: absolute;
      transition: opacity 100ms $animationCurve;
      opacity: 1;

      &--hidden {
        opacity: 0;
      }
    }

    .middle-handle__left-curve {
      left: -8px;
      top: 12px;
      height: 8px;
      width: 8px;
    }

    .middle-handle__right-curve {
      right: -8px;
      top: 12px;
      height: 8px;
      width: 8px;
      transform: rotate(90deg);
    }
  }

  &__gripper {
    cursor: v-bind(handleCursor);
  }

  &__loop-duration {
    cursor: v-bind(pointerCursor);
  }

  &__duration-input {
    background: none;
    width: 100%;
    text-align: center;
    font-size: 11px;
    border: none;
    -webkit-appearance: none;
    appearance: none;
    outline: none;
    cursor: v-bind(textCursor)
  }

  &__inner-bracket {
    border: 3px solid $vibrant-yellow;
    position: absolute;
    left: v-bind(leftInnerBracketStyle);
    right: v-bind(rightInnerBracketStyle);
    top: 0px;
    bottom: 0px;
    pointer-events: none;
    border-radius: 8px;
  }

  &__bracket {
    border-top: 3px solid $vibrant-yellow;
    border-bottom: 3px solid $vibrant-yellow;
    position: absolute;
    left: v-bind(leftBracketStyle);
    right: v-bind(rightBracketStyle);
    top: 0px;
    bottom: 0px;
    pointer-events: none;
    border-radius: 10px;
  }

  &__handle {
    position: absolute;
    background-color: $vibrant-yellow;
    width: v-bind(handleWidthStyle);
    top: 0px;
    bottom: 0px;
    cursor: v-bind(handleCursor);

    &--left {
      left: v-bind(leftBracketStyle);
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
    }

    &--right {
      right: v-bind(rightBracketStyle);
      border-top-right-radius: 10px;
      border-bottom-right-radius: 10px;
    }
  }

  &__time-label {
    position: absolute;
    color: $vibrant-yellow;
    font-size: 11px;
    padding: 3px;
    width: 28px;
  }

  &__grabber {
    position: absolute;
    top: calc(50% - 10px);
    left: 4px;
    height: 20px;
    width: 2px;
    background-color: $grey-90;
    border-radius: 10px;
  }

  &__outside-loop-overlay {
    position: absolute;
    height: 74px;
    top: 0px;
    opacity: 90%;
    background-color: $grey-90;

    &--left {
      width: v-bind(loopStartPositionStyle);
      left: 0px;
    }

    &--right {
      right: 0px;
      width: v-bind(loopEndPositionStyle)
    }
  }

  &__playhead-overlay {
    position: absolute;
    height: 100%;
    top: 0;
    left: v-bind(playHeadOverlayStartPositionStyle);
    right: v-bind(loopEndPositionStyle);
    background-color: rgba(22, 22, 22, 0.58);
  }

  &__playhead-hover-overlay {
    position: absolute;
    height: 100%;
    top: 0;
    background-color: rgba(22, 22, 22, 0.20);
    left: v-bind(loopStartPositionStyle);
    right: v-bind(playHeadHoverOverlayEndPosition);
  }
}
</style>